import React, { useEffect, useState, useCallback } from 'react';
import { Hourglass, X } from 'lucide-react';

const STORAGE_KEY = 'writeright_whitespace_state';
const SNOOZE_MINUTES = 30;
const MIN_INTERVAL_MS = 60 * 60 * 1000; // Max 1 shown per hour total
const NEXT_PATIENT_THRESHOLD = 3;
const UNSUBMITTED_THRESHOLD = 2;
const TIME_SINCE_SUBMIT_MINUTES = 45;
const OPTOUT_KEY = 'writeright_whitespace_disabled';
const PREVIEW_FLAG = 'writeright_whitespace_force_preview';

// Allow profile settings to fire a preview by setting localStorage flag.
// Call from the profile "Preview encouragement" button.
export const forcePreviewBanner = () => {
  try {
    localStorage.setItem(PREVIEW_FLAG, '1');
    window.dispatchEvent(new CustomEvent('writeright:force-whitespace-preview'));
  } catch { /* ignore */ }
};

/**
 * WhiteSpaceBanner
 * Non-blocking top-banner reminder for providers mid-clinic.
 * Named after the provider's own phrase — the "white space" gap between patients.
 * Headline is the call-to-action itself: "Ready to submit?"
 *
 * Fires when ALL of:
 *   - Provider has hit "Next Patient" / "New Tab" ≥ NEXT_PATIENT_THRESHOLD times in a row
 *     (without submitting anything in between)
 *   - There are ≥ UNSUBMITTED_THRESHOLD unsubmitted finished notes from today
 *   - Last Submitted-to-Genesis tick was > TIME_SINCE_SUBMIT_MINUTES ago
 *   - A banner hasn't shown in the last hour (MIN_INTERVAL_MS)
 *   - User hasn't disabled the feature in settings (OPTOUT_KEY)
 */
const WhiteSpaceBanner = ({ history, effectiveRole, onShowNeedsSubmit }) => {
  const [visible, setVisible] = useState(false);

  const readState = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : { next_clicks: 0, last_shown_at: 0, snooze_until: 0 };
    } catch {
      return { next_clicks: 0, last_shown_at: 0, snooze_until: 0 };
    }
  };

  const writeState = (patch) => {
    try {
      const current = readState();
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...current, ...patch }));
    } catch { /* ignore */ }
  };

  const countUnsubmittedToday = useCallback(() => {
    if (!Array.isArray(history)) return 0;
    const today = new Date().toDateString();
    return history.filter(h => {
      if (h.completed || h.completed_and_submitted) return false;
      if (h.is_draft) return false;
      const ts = new Date(h.timestamp || h.created_at || 0);
      if (ts.toDateString() !== today) return false;
      return !!(h.clinical_note || h.admin_letter || h.indication);
    }).length;
  }, [history]);

  const latestSubmitMs = useCallback(() => {
    if (!Array.isArray(history)) return 0;
    let latest = 0;
    for (const h of history) {
      if (h.submitted_at) {
        const t = new Date(h.submitted_at).getTime();
        if (t > latest) latest = t;
      }
    }
    return latest;
  }, [history]);

  const checkShouldShow = useCallback(() => {
    if (effectiveRole !== 'provider') return false;
    if (localStorage.getItem(OPTOUT_KEY) === 'true') return false;
    const state = readState();
    const now = Date.now();
    if (state.snooze_until && now < state.snooze_until) return false;
    if (state.last_shown_at && now - state.last_shown_at < MIN_INTERVAL_MS) return false;
    if ((state.next_clicks || 0) < NEXT_PATIENT_THRESHOLD) return false;
    const unsubmitted = countUnsubmittedToday();
    if (unsubmitted < UNSUBMITTED_THRESHOLD) return false;
    const lastSubmitMs = latestSubmitMs();
    if (lastSubmitMs && (now - lastSubmitMs) < TIME_SINCE_SUBMIT_MINUTES * 60 * 1000) return false;
    return true;
  }, [effectiveRole, countUnsubmittedToday, latestSubmitMs]);

  // Force-preview hook (profile settings "Preview encouragement" button)
  useEffect(() => {
    const forcePreview = () => {
      try { localStorage.removeItem(PREVIEW_FLAG); } catch { /* ignore */ }
      setVisible(true);
    };
    window.addEventListener('writeright:force-whitespace-preview', forcePreview);
    // If flag was set before mount (e.g., from a previous click), honour it
    if (localStorage.getItem(PREVIEW_FLAG) === '1') {
      try { localStorage.removeItem(PREVIEW_FLAG); } catch { /* ignore */ }
      setVisible(true);
    }
    return () => window.removeEventListener('writeright:force-whitespace-preview', forcePreview);
  }, []);

  // Increment counter whenever someone fires next-patient / new-tab
  useEffect(() => {
    const bump = () => {
      const state = readState();
      writeState({ next_clicks: (state.next_clicks || 0) + 1 });
      if (checkShouldShow()) {
        writeState({ last_shown_at: Date.now() });
        setVisible(true);
      }
    };
    window.addEventListener('writeright:next-patient', bump);
    window.addEventListener('writeright:new-tab', bump);
    return () => {
      window.removeEventListener('writeright:next-patient', bump);
      window.removeEventListener('writeright:new-tab', bump);
    };
  }, [checkShouldShow]);

  // Reset the counter whenever a note is Submitted to Genesis
  useEffect(() => {
    const reset = () => {
      writeState({ next_clicks: 0 });
      setVisible(false);
    };
    window.addEventListener('writeright:note-submitted', reset);
    return () => window.removeEventListener('writeright:note-submitted', reset);
  }, []);

  const snooze = () => {
    writeState({ snooze_until: Date.now() + SNOOZE_MINUTES * 60 * 1000 });
    setVisible(false);
  };

  const handleShow = () => {
    writeState({ next_clicks: 0, snooze_until: Date.now() + SNOOZE_MINUTES * 60 * 1000 });
    setVisible(false);
    if (onShowNeedsSubmit) onShowNeedsSubmit();
  };

  if (!visible) return null;

  const unsubmitted = countUnsubmittedToday();

  return (
    <div
      className="sticky top-0 z-40 mx-2 mt-2 rounded-lg border border-sky-500/40 bg-sky-950/85 px-3 py-2 shadow-lg"
      data-testid="white-space-banner"
    >
      <div className="flex items-start gap-2">
        <Hourglass className="w-4 h-4 text-sky-400 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold text-sky-300">
            Ready to submit?
          </p>
          <p className="text-[11px] text-sky-200/80 mt-0.5">
            Got a minute of white space? {unsubmitted} note{unsubmitted !== 1 ? 's' : ''} from today ready to close out.
          </p>
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            <button
              onClick={handleShow}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold bg-sky-500/25 border border-sky-500/50 text-sky-100 hover:bg-sky-500/40 transition-colors"
              data-testid="white-space-show-btn"
            >
              Show me
            </button>
            <button
              onClick={snooze}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold bg-secondary/60 border border-border text-foreground/80 hover:bg-secondary transition-colors"
              data-testid="white-space-later-btn"
            >
              Later
            </button>
          </div>
        </div>
        <button
          onClick={snooze}
          className="p-1 text-sky-300/60 hover:text-sky-300 rounded"
          title="Dismiss (will ask again later)"
          data-testid="white-space-dismiss-btn"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default WhiteSpaceBanner;
